import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { cn } from '@rocco/utils/cn';
import { Image } from '@rocco/components/image';
import { isExternalURL, isSameDomainURL } from '~/modules/url';
import { isSingleGiphyUrl } from '~/modules/giphy';
import type { LinkPreview, TextOrFileMessage } from '../../types';
import { extractChatPreviewUrls } from '../../utils';
import { useLinkPreviews } from '../../hooks/use-link-previews';
import { ExternalLinkDialog } from './ExternalLinkDialog';

/**
 * Link previews for URLs in a text or file message.
 * @see {@link ChatMessageBubble}
 */
export function ChatMessageLinkPreviews({
    className,
    message,
    isOutgoing,
}: {
    className?: string;
    message: TextOrFileMessage;
    isOutgoing: boolean;
}) {
    const text = message.Content;
    const supportedLinks = useMemo(() => {
        const links = text ? extractChatPreviewUrls(text) : [];
        return links.filter((url) => !isSingleGiphyUrl(url));
    }, [text]);
    const linkPreviews = useLinkPreviews(supportedLinks, message.isOptimistic);

    if (!linkPreviews.length) {
        return null;
    }

    return (
        <ul className={cn('flex flex-col gap-[8px]', className)}>
            {linkPreviews.map((preview) => (
                <LinkPreviewItem
                    key={preview.originalUrl}
                    preview={preview}
                    isOutgoing={isOutgoing}
                />
            ))}
        </ul>
    );
}

function LinkPreviewItem({
    preview,
    isOutgoing,
}: {
    preview: LinkPreview;
    isOutgoing: boolean;
}) {
    const isMultiplied = preview.type !== 'external' && preview.isMultiplied;
    const [isExternalWarningOpen, setIsExternalWarningOpen] = useState(false);
    const [isFaviconHidden, setIsFaviconHidden] = useState(false);
    const isSameDomain = useMemo(() => {
        return isSameDomainURL(preview.href);
    }, [preview.href]);
    const isExternal = useMemo(() => {
        return (
            !isOutgoing &&
            preview.type === 'external' &&
            isExternalURL(preview.href)
        );
    }, [isOutgoing, preview]);

    return (
        <li className="w-full">
            <Link
                to={preview.href}
                target={!isSameDomain ? '_blank' : undefined}
                rel={!isSameDomain ? 'noopener noreferrer' : undefined}
                className="flex min-h-[72px] w-full items-center gap-[8px] rounded-[8px] bg-white p-[8px] text-black hover:bg-black/10"
                onClick={(event) => {
                    if (isExternal) {
                        event.preventDefault();
                        setIsExternalWarningOpen(true);
                    }
                }}
            >
                {preview.imageUrl ? (
                    <span
                        className={cn(
                            'rounded-[4px]',
                            isMultiplied && 'bg-[var(--color-clearcut-fill)]',
                        )}
                    >
                        <Image
                            src={preview.imageUrl}
                            alt={preview.title}
                            className={cn(
                                'h-[56px] shrink-0 rounded-[inherit]',
                                preview.type === 'external'
                                    ? 'w-[84px]'
                                    : 'w-[56px]',
                                preview.type === 'professional' || isMultiplied
                                    ? 'object-contain'
                                    : 'object-cover',
                                isMultiplied ? 'bg-transparent' : 'bg-white',
                            )}
                            style={{
                                backgroundColor:
                                    preview.type !== 'external'
                                        ? preview.logoBackgroundColor
                                        : undefined,
                            }}
                            multiplied={isMultiplied}
                            fadeIn={false}
                            loading="lazy"
                        />
                    </span>
                ) : preview.type !== 'external' ? (
                    <div className="size-[56px] shrink-0 rounded-[4px] bg-black/10"></div>
                ) : null}
                <div className="min-w-0 flex-1 space-y-[4px] leading-tight">
                    {preview.type !== 'external' && (
                        <div className="text-[10px] tracking-wide uppercase opacity-80">
                            {preview.type}
                        </div>
                    )}
                    <div
                        className={cn(
                            'text-[13px] font-medium',
                            preview.type === 'external' && preview.imageUrl
                                ? 'line-clamp-2'
                                : 'truncate',
                        )}
                    >
                        {preview.title}
                    </div>
                    <div className="flex items-center gap-[4px]">
                        {preview.type === 'external' &&
                            !preview.imageUrl &&
                            preview.faviconUrl &&
                            !isFaviconHidden && (
                                <img
                                    src={preview.faviconUrl}
                                    alt={preview.hostname}
                                    className="size-[12px] shrink-0"
                                    loading="lazy"
                                    // The favicon URL may not be a valid URL.
                                    onError={() => setIsFaviconHidden(true)}
                                />
                            )}
                        <span className="min-w-0 flex-1 truncate text-[12px] opacity-80">
                            {preview.type === 'external'
                                ? preview.hostname
                                : preview.subtitle}
                        </span>
                    </div>
                    {/* Show external link description if no image. */}
                    {!preview.imageUrl &&
                        preview.type === 'external' &&
                        preview.description && (
                            <div className="truncate text-[12px] opacity-80">
                                {preview.description}
                            </div>
                        )}
                </div>
            </Link>
            {isExternal && (
                <ExternalLinkDialog
                    open={isExternalWarningOpen}
                    url={preview.href}
                    onOpenChange={setIsExternalWarningOpen}
                />
            )}
        </li>
    );
}
